import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { FileCheck } from "lucide-react";
import {
  COMMERCIAL_DOCUMENT_LABELS,
  CommercialDocumentFilingSchema,
  commercialFilingRegister,
  type CommercialDocument,
  type CommercialDocumentFiling,
} from "../../shared/commercial-documents";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Field, FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { documentDate } from "../knowledge/customer-document-format";
import { fileSignedCommercialDocument } from "./commercial-documents-api";
import { uploadCustomerDocumentFile } from "./customer-documents-api";

const registerName = (kind: CommercialDocument["kind"]) =>
  commercialFilingRegister(kind).kind === "quote" ? "Quote documents" : "Agreements";

export function FiledNotice({
  record,
  filing,
}: {
  record: CommercialDocument;
  filing: CommercialDocumentFiling;
}) {
  return (
    <div role="status" className="flex items-start gap-3 rounded-md border bg-card p-4">
      <FileCheck className="mt-0.5 size-5 shrink-0 text-link" />
      <div className="min-w-0 space-y-1 text-sm">
        <p className="font-semibold">
          Signed {documentDate(filing.signedOn)} · filed in {registerName(record.kind)}
        </p>
        <p className="text-muted-foreground">
          Version {filing.revision} is the signed{" "}
          {COMMERCIAL_DOCUMENT_LABELS[record.kind].toLowerCase()} and can no longer change. Filed by{" "}
          {filing.filedBy}. Duplicate it to start a new version.
        </p>
      </div>
    </div>
  );
}

export function FileSignedDialog({
  slug,
  record,
  onClose,
  onFiled,
}: {
  slug: string;
  record: CommercialDocument;
  onClose: () => void;
  onFiled: (filing: CommercialDocumentFiling) => void;
}) {
  const [signedOn, setSignedOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [file, setFile] = useState<File | null>(null);
  const label = COMMERCIAL_DOCUMENT_LABELS[record.kind].toLowerCase();
  const submit = useMutation({
    mutationFn: async (signed: { file: File; signedOn: string }) => {
      const uploaded = await uploadCustomerDocumentFile(
        slug,
        commercialFilingRegister(record.kind).kind,
        signed.file,
      );
      const response = await fileSignedCommercialDocument(slug, record.id, {
        revision: record.revision,
        fileId: uploaded.fileId,
        signedOn: signed.signedOn,
      });
      return CommercialDocumentFilingSchema.parse(response.filing);
    },
    onSuccess: onFiled,
  });
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !submit.isPending) onClose();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>File signed copy</DialogTitle>
          <DialogDescription>
            {record.title} · {record.reference} · version {record.revision}
          </DialogDescription>
        </DialogHeader>
        <DialogForm
          aria-label="File signed copy"
          onSubmit={(event) => {
            event.preventDefault();
            if (file && signedOn) submit.mutate({ file, signedOn });
          }}
        >
          <DialogBody>
            <SignedCopyFields
              busy={submit.isPending}
              setFile={setFile}
              signedOn={signedOn}
              setSignedOn={setSignedOn}
            />
            <p className="text-sm text-muted-foreground">
              The PDF is filed in {registerName(record.kind)} as{" "}
              {commercialFilingRegister(record.kind).status}, kept internal until you publish it.
              Version {record.revision} of this {label} is then locked; duplicate it to make
              changes.
            </p>
            {submit.error ? <FieldError>{submit.error.message}</FieldError> : null}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" disabled={submit.isPending} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submit.isPending || !file || !signedOn}>
              {submit.isPending ? "Filing…" : "File signed copy"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

function SignedCopyFields({
  busy,
  setFile,
  signedOn,
  setSignedOn,
}: {
  busy: boolean;
  setFile: (file: File | null) => void;
  signedOn: string;
  setSignedOn: (value: string) => void;
}) {
  return (
    <fieldset disabled={busy} className="space-y-4">
      <Field>
        <FieldLabel htmlFor="signed-copy-file">Signed PDF</FieldLabel>
        <Input
          id="signed-copy-file"
          type="file"
          accept="application/pdf,.pdf"
          onChange={(event) => {
            setFile(event.target.files?.[0] ?? null);
          }}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="signed-copy-date">Signed on</FieldLabel>
        <Input
          id="signed-copy-date"
          type="date"
          value={signedOn}
          onChange={(event) => {
            setSignedOn(event.target.value);
          }}
        />
      </Field>
    </fieldset>
  );
}
